// src-lit/components/product/kd-product-badge-group.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { onEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';

interface BadgeVariantEntry {
  /** 「販売期限間近」(sales_deadline ≤ 10日)。課題 #58 で在庫による分岐を廃止し 1 種類に統合 */
  isSalesDeadlineClose: boolean;
  /** パントリー「#残りわずか」(pantry variant かつ inventory 1-10) */
  isPantryLowStock: boolean;
  /** ポイント還元率（'0.5' / '0.25' / ''）。空文字はポイント対象外 */
  pointRate: string;
  /** 「#背景タイプ」のおトクな背景 type_handle（空文字はおトクな背景なし） */
  dealReasonHashtagName: string;
}
type BadgeVariantMap = Record<string, BadgeVariantEntry | undefined>;

/**
 * @element kd-product-badge-group
 * @description バリアント選択に応じて variant 依存のバッジを動的更新する。
 *   kd-product-variant-selected イベントを購読し、以下を更新する:
 *   - 「販売期限間近」(error) の表示/非表示
 *   - パントリー「#残りわずか」tag の表示/非表示
 *   - 「ポイント還元率」バッジの表示/非表示 + ラベル
 *   - 「#背景タイプ」tag の表示/非表示 + ラベル（おトクな背景 type_handle、variant 単位）
 *   - 「内容量×商品セット名」バッジの表示/非表示 + ラベル（option1 が variant 依存）
 *
 *   「残りわずか + 残り{N}個」(low-stock-badge) は stockLimitedQuantity（product 全体・
 *   パントリー除外）由来で variant 切替で値が変わらないため、ここでは更新しない。
 */
@customElement('kd-product-badge-group')
export class KdProductBadgeGroup extends LitElement {
  createRenderRoot() {
    return this;
  }

  private unsubscribe: (() => void) | null = null;

  private variantMap: BadgeVariantMap = {};

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.loadVariantMap();
    this.unsubscribe = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => this.handleVariantSelected(e.detail),
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private loadVariantMap(): void {
    const script = this.querySelector<HTMLScriptElement>(
      SELECTORS.PRODUCT_BADGE_GROUP.VARIANT_MAP,
    );
    if (!script?.textContent) return;
    try {
      this.variantMap = JSON.parse(script.textContent);
    } catch {
      // JSON parse 失敗時は空マップのまま
    }
  }

  private handleVariantSelected(detail: VariantSelectedDetail): void {
    const entry = this.variantMap[detail.variantId];
    if (!entry) return;

    this.toggleVisibility(
      SELECTORS.PRODUCT_BADGE_GROUP.SALES_DEADLINE_CLOSE,
      entry.isSalesDeadlineClose,
    );
    this.toggleVisibility(
      SELECTORS.PRODUCT_BADGE_GROUP.PANTRY_LOW_STOCK,
      entry.isPantryLowStock,
    );
    this.updatePoint(entry.pointRate);
    this.updateDealReasonHashtag(entry.dealReasonHashtagName);
  }

  private toggleVisibility(selector: string, visible: boolean): void {
    const el = this.querySelector<HTMLElement>(selector);
    if (el) el.hidden = !visible;
  }

  private updatePoint(rate: string): void {
    const wrapper = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_BADGE_GROUP.POINT,
    );
    if (!wrapper) return;

    if (rate === '') {
      wrapper.hidden = true;
      return;
    }

    wrapper.hidden = false;
    // v2-badge は <span class="...">label</span> という単一の要素を出力する前提。
    // ラベル文字列だけを差し替えれば、クラス・アイコン等は維持される。
    const badgeSpan = wrapper.firstElementChild;
    if (badgeSpan instanceof HTMLElement) {
      badgeSpan.textContent = `ポイント${rate}%`;
    }
  }

  private updateDealReasonHashtag(name: string): void {
    const li = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_BADGE_GROUP.DEAL_REASON_HASHTAG,
    );
    if (!li) return;

    if (!name) {
      li.hidden = true;
      return;
    }

    li.hidden = false;
    // 予約 <li><span>#name</span></li>（ハンドロール）の構造。ラベルだけ差し替え、クラスは維持。
    // 課題 #75 でリンクを外したため href の更新は行わない。
    const label = li.querySelector('span');
    if (label) label.textContent = `#${name}`;
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'kd-product-badge-group': KdProductBadgeGroup;
  }
}
